import React, { useState, useEffect } from 'react';
import { FiCheckCircle, FiXCircle } from 'react-icons/fi';
import { showErrorToast, showSuccessToast } from '../../../../../../components/Constant/Notification';
import ProspectPropertyService from '../../../../../Service/ProspectPropertyService';
import { userUid } from '../../../../../../utils/storage';
import ConfirmationModal from '../../../../../../components/shareableContainer/ConfirmationModal';

const PhysicalInspection = ({ propertyId, setPropertyData, setTriggerFetch, assignedUserData, isDropped }) => {
  // State management for checkbox selection
  const [physicalInspectionCleared, setPhysicalInspectionCleared] = useState(false);
  const [remarks, setRemarks] = useState('');
  const [modifiedBy, setModifiedBy] = useState({});

  // Modal state management
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);

  // Fetch data and initialize state
  const fetchData = async () => {
    try {
      const response = await ProspectPropertyService.getStageWiseProperties({
        propertyId: propertyId,
        stage: 'Analysis',
      });
      const data = response.data?.data;
      if (data && data.physicalInspectionRecords && data.physicalInspectionRecords.records) {
        const physicalRecords = data.physicalInspectionRecords.records;
        // Assuming the first record contains the relevant data
        if (physicalRecords.length > 0) {
          const record = physicalRecords[0]; // Use the first record for initialization
          setPhysicalInspectionCleared(record.physicalInspectionIsAllClear || false);
          setRemarks(record.notes || '');
          setModifiedBy(record.modifiedBy || {});
        }
      }
    } catch (error) {
      console.error('Error loading Physical Inspection data:', error);
      showErrorToast('Failed to load Physical Inspection data');
    }
  };

  // Fetch data on component mount
  useEffect(() => {
    fetchData();
  }, [propertyId]);

  // Handler functions
  const handleCheckboxClick = () => {
    setPendingAction('physicalInspection');
    setShowConfirmModal(true);
  };

  const handleConfirmAction = () => {
    setPhysicalInspectionCleared(true);
   // showSuccessToast('Physical Inspection marked as clear');
    setShowConfirmModal(false);
    setPendingAction(null);
  };

  const handleCloseModal = () => {
    setShowConfirmModal(false);
    setPendingAction(null);
  };

  const handleUndo = () => {
    setPhysicalInspectionCleared(false);
   // showSuccessToast('Physical Inspection status cleared');
  };

  const handleSave = async () => {
    try {
      // Construct the analysisData array, only including marked items
      const analysisData = [
        {
          type: 'Physical Inspection is all clear',
          isCompleted: physicalInspectionCleared,
          fieldUpdatedBy: physicalInspectionCleared ? userUid : undefined,
        },
      ]

      // Validate that there is at least one marked analysis or remarks
      if (analysisData.length === 0 && !remarks) {
        showErrorToast('Please mark Physical Inspection or provide remarks');
        return;
      }

      // Construct the payload
      const dataToSave = {
        propertyId: propertyId,
        stageType: 'Physical Inspection',
        analysisData: analysisData,
        updatedBy: userUid,
        notes: remarks || '',
      };

      // Make the API call to save the data
      const response = await ProspectPropertyService.analysisDocumentMarkUpdate(dataToSave);
      if (response.status === 200 || response.status === 201) {
        showSuccessToast('Physical Inspection saved successfully');
        setTriggerFetch(prev => !prev);
        await fetchData();
      } else {
        showErrorToast('Failed to save Physical Inspection');
      }
    } catch (error) {
      showErrorToast('Failed to save Physical Inspection');
      console.error('Error saving Physical Inspection:', error);
    }
  };

  return (
    <div className="">
      <div>
        <div className="text-sm flex gap-3 w-[70%] border border-gray-300 p-3 rounded-[10px] text-slate-500">
          <div>Assigned Front end person - </div>
          <span className="text-blue-500">{assignedUserData[4]?.name}</span>
        </div>

        <div className="">
          <div className="w-[70%] flex flex-col gap-2 mt-4">
            {/* Physical Inspection Clear Button */}
            <div className="flex items-center gap-2 mt-4">
              <button
                onClick={handleCheckboxClick}
                className={`flex-1 px-4 py-2 rounded-md flex items-center justify-center gap-2 transition-colors ${
                  physicalInspectionCleared
                    ? 'bg-green-100 text-green-700 border border-green-300'
                    : isDropped
                    ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                    : 'bg-gray-200 text-orange-500 hover:bg-gray-300'
                }`}
                disabled={physicalInspectionCleared || isDropped}
              >
                {physicalInspectionCleared && <FiCheckCircle className="text-green-600" />}
                <span>Physical Inspection is all clear</span>
                {physicalInspectionCleared && modifiedBy?.name && (
                  <span className="text-xs text-green-600 font-medium ml-2">
                    (Marked by "{modifiedBy.name} ({modifiedBy.role})")
                  </span>
                )}
              </button>
              {physicalInspectionCleared && (
                <button 
                  onClick={handleUndo} 
                  disabled={isDropped}
                  className={`${isDropped ? 'text-gray-400 cursor-not-allowed' : 'text-blue-500'}`}
                >
                  Undo
                </button>
              )}
            </div>
          </div>

          <div className="w-[70%] flex flex-col gap-2 mt-4">
            <label htmlFor="remarks">Any additional Remarks to note</label>
            <textarea
              id="remarks"
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              cols="30"
              rows="4"
              disabled={isDropped}
              className={`w-full border rounded-md mt-2 p-2 ${
                isDropped 
                  ? 'border-gray-300 bg-gray-100 text-gray-500 cursor-not-allowed' 
                  : 'border-gray-300'
              }`}
              placeholder="Enter any additional remarks or notes..."
            />
            <button
              onClick={handleSave}
              disabled={isDropped}
              className={`mt-4 w-full px-4 py-2 rounded-[10px] transition-colors ${
                isDropped 
                  ? 'bg-gray-400 text-gray-600 cursor-not-allowed' 
                  : 'bg-customGreen text-white hover:bg-green-600'
              }`}
            >
              Save
            </button>
          </div>
        </div>
      </div>

      <ConfirmationModal
        showModal={showConfirmModal}
        onCancel={handleCloseModal}
        onConfirm={handleConfirmAction}
        title="Are You Sure?"
        message="Are you sure you want to mark Physical Inspection as clear?"
        confirmText="Yes, I'm sure"
      />
    </div>
  );
};

export default PhysicalInspection;